// src/pages/Expedientes/PartesTab.js
import React, { useEffect, useState, useRef, useCallback } from "react";
import {
    Box, Card, CardHeader, CardContent, Button, IconButton, Dialog,
    DialogTitle, DialogContent, DialogActions, TextField, Collapse,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    Tooltip, CircularProgress, Snackbar, Grid, Divider, Typography
} from "@mui/material";
import MuiAlert from '@mui/material/Alert';
import { Edit, Delete, Add, ExpandMore, ExpandLess, PersonAdd } from "@mui/icons-material";
import Autocomplete from '@mui/material/Autocomplete';
import { capitalizar } from "../../utils/capitalizar";
import {
    expedientePartesJWT, crearParteJWT, editarParteJWT, eliminarParteJWT,
    getCombosJWT, crearContraparteJWT, getReparticionesParteJWT
} from "../../api/endpoints";
import usePermisosModulo from "../../hooks/usePermisosModulo";

// Normaliza keys de SQL Server (Caracter -> caracter, Id_Expediente -> id_expediente)
function normalizeKeys(obj) {
    if (!obj || typeof obj !== 'object') return obj;
    if (Array.isArray(obj)) return obj.map(normalizeKeys);
    const out = {};
    for (const [k, v] of Object.entries(obj)) {
        out[k.toLowerCase()] = v;
    }
    return out;
}

function Alert(props) { return <MuiAlert elevation={6} variant="filled" {...props} />; }

const TABS = [
    { key: "provincia", label: "Provincia de San Juan", grilla: 1 },
    { key: "contraparte", label: "Contra Parte", grilla: 2 },
    { key: "auxiliares", label: "Auxiliares del Proceso", grilla: 3 },
];

export default function PartesTab({ idExpediente, usuario, modulo }) {
    const [tabOpen, setTabOpen] = useState({ provincia: true, contraparte: true, auxiliares: true });

    const [partes, setPartes] = useState({ provincia: [], contraparte: [], auxiliares: [] });
    const [caracteres, setCaracteres] = useState([]);
    const [loading, setLoading] = useState(false);

    const [modalOpen, setModalOpen] = useState(false);
    const [editData, setEditData] = useState(null);

    // Autocomplete sources
    const [personas, setPersonas] = useState([]); const [loadingPersonas, setLoadingPersonas] = useState(false);
    const [reparticiones, setReparticiones] = useState([]); const [loadingReparticiones, setLoadingReparticiones] = useState(false);
    const [contrapartes, setContrapartes] = useState([]); const [loadingContrapartes, setLoadingContrapartes] = useState(false);

    // Permisos por grilla (provincia vs contraparte/auxiliares) — hook con caché
    const { permDe, ready: permReady } = usePermisosModulo(modulo);
    const permProvincia = permDe("PESTAÑA PARTES - GRILLA PROVINCIA DE SAN JUAN", "PESTAÑA PARTES");
    const permContraparte = permDe("PESTAÑA PARTES - GRILLA CONTRAPARTE Y AUXILIARES DEL PROCESO", "PESTAÑA PARTES");

    // Bloqueo por Plan de Pago DGR (diferenciado por grilla)
    const [bloqueadoProv, setBloqueadoProv] = useState(false);
    const [bloqueadoCont, setBloqueadoCont] = useState(false);

    // UI
    const [snack, setSnack] = useState({ open: false, msg: "", severity: "success" });
    const [confirmOpen, setConfirmOpen] = useState(false);
    const [toDelete, setToDelete] = useState(null);

    // Helper: permisos según grilla
    const permParaGrilla = (grilla) => grilla === 1 ? permProvincia : permContraparte;
    // Helper: bloqueo PP según grilla
    const bloqueadoParaGrilla = (grilla) => grilla === 1 ? bloqueadoProv : bloqueadoCont;

    // ---------- DATA ----------
    const refrescarPartes = useCallback(async () => {
        setLoading(true);
        try {
            const data = await expedientePartesJWT(idExpediente);
            if (data && data.ok) {
                setPartes({
                    provincia: (data.provincia || []).map(normalizeKeys),
                    contraparte: (data.contraparte || []).map(normalizeKeys),
                    auxiliares: (data.auxiliares || []).map(normalizeKeys),
                });
                setBloqueadoProv(!!data.bloqueado_provincia);
                setBloqueadoCont(!!data.bloqueado_contraparte);
            }
        } finally {
            setLoading(false);
        }
    }, [idExpediente]);

    useEffect(() => {
        refrescarPartes();
        (async () => {
            const c = await getCombosJWT('caracteres');
            setCaracteres(c?.items || []);
        })();
    }, [idExpediente, refrescarPartes]);

    // --- AUTOCOMPLETE FETCHS ---
    async function fetchPersonas(q) {
        if ((q || "").length < 2) return;
        setLoadingPersonas(true);
        try {
            const data = await getCombosJWT('personal', q);
            setPersonas(data?.items || []);
        } finally { setLoadingPersonas(false); }
    }
    // Filtrada por módulo del expediente y carácter, como el combo del sistema viejo
    async function fetchReparticiones(q, idCaracter) {
        if ((q || "").length < 2) return;
        setLoadingReparticiones(true);
        try {
            const data = await getReparticionesParteJWT(idExpediente, idCaracter, q);
            setReparticiones(data?.items || []);
        } finally { setLoadingReparticiones(false); }
    }
    async function fetchContrapartes(q) {
        if ((q || "").length < 2) return;
        setLoadingContrapartes(true);
        try {
            const data = await getCombosJWT('contraparte', q);
            setContrapartes(data?.items || []);
        } finally { setLoadingContrapartes(false); }
    }

    // --- CRUD ---
    async function crearParte(data) {
        return crearParteJWT(idExpediente, {
            grilla: data.grilla,
            id_caracter: data.id_caracter,
            id_personal: data.id_personal || null,
            id_reparticion: data.id_reparticion || null,
            id_contraparte: data.id_contraparte || null,
            direccion: data.direccion || null,
            cp: data.cp || null,
        });
    }

    async function editarParte(data) {
        return editarParteJWT(idExpediente, data.id, {
            grilla: data.grilla,
            id_caracter: data.id_caracter,
            id_personal: data.id_personal || null,
            id_reparticion: data.id_reparticion || null,
            id_contraparte: data.id_contraparte || null,
            direccion: data.direccion || null,
            cp: data.cp || null,
        });
    }

    async function eliminarParte(id) {
        return eliminarParteJWT(idExpediente, id);
    }

    // Limpiar opciones de búsqueda al abrir modal (evita que resultados de una búsqueda anterior aparezcan en otro campo)
    const limpiarBusquedas = () => {
        setPersonas([]);
        setReparticiones([]);
        setContrapartes([]);
    };

    const handleEdit = (grupo, parte, grilla) => {
        if (bloqueadoParaGrilla(grilla)) {
            setSnack({ open: true, msg: "Bloqueado por Plan de Pago DGR pendiente de auditoría.", severity: "warning" });
            return;
        }
        const p = permParaGrilla(grilla);
        if (!p.update) {
            setSnack({ open: true, msg: "No tenés permiso para editar.", severity: "warning" });
            return;
        }
        limpiarBusquedas();
        setEditData({ ...parte, grupo, grilla });
        setModalOpen(true);
    };

    const handleAdd = (grupo, grilla) => {
        if (bloqueadoParaGrilla(grilla)) {
            setSnack({ open: true, msg: "Bloqueado por Plan de Pago DGR pendiente de auditoría.", severity: "warning" });
            return;
        }
        const p = permParaGrilla(grilla);
        if (!p.insert) {
            setSnack({ open: true, msg: "No tenés permiso para agregar.", severity: "warning" });
            return;
        }
        limpiarBusquedas();
        setEditData({ grupo, grilla });
        setModalOpen(true);
    };

    const handleSave = async (data) => {
        const payload = {
            grilla: data.grilla,
            id_caracter: data.id_caracter,
            id_personal: data.id_personal,
            id_reparticion: data.id_reparticion,
            id_contraparte: data.id_contraparte,
            direccion: data.direccion,
            cp: data.cp,
        };

        try {
            if (data.id) {
                const r = await editarParte({ ...payload, id: data.id });
                if (r?.success || r?.ok) {
                    await refrescarPartes();
                    setSnack({ open: true, msg: "Registro actualizado.", severity: "success" });
                    setModalOpen(false);
                } else {
                    setSnack({ open: true, msg: r?.mensaje || "Error al editar.", severity: "error" });
                }
            } else {
                const r = await crearParte(payload);
                if (r?.success || r?.ok) {
                    await refrescarPartes();
                    setSnack({ open: true, msg: "Registro creado.", severity: "success" });
                    setModalOpen(false);
                } else {
                    setSnack({ open: true, msg: r?.mensaje || "Error al crear.", severity: "error" });
                }
            }
        } catch (err) {
            setSnack({ open: true, msg: err?.message || "Error de red.", severity: "error" });
        }
    };

    const askDelete = (grupo, row) => {
        const grilla = grupo === "provincia" ? 1 : (grupo === "contraparte" ? 2 : 3);
        if (bloqueadoParaGrilla(grilla)) {
            setSnack({ open: true, msg: "Bloqueado por Plan de Pago DGR pendiente de auditoría.", severity: "warning" });
            return;
        }
        const p = permParaGrilla(grilla);
        if (!p.delete) {
            setSnack({ open: true, msg: "No tenés permiso para eliminar.", severity: "warning" });
            return;
        }
        setToDelete({ grupo, row });
        setConfirmOpen(true);
    };

    const confirmDelete = async () => {
        if (!toDelete) return;
        try {
            const r = await eliminarParte(toDelete.row.id);
            if (r?.success || r?.ok) {
                await refrescarPartes();
                setSnack({ open: true, msg: "Registro eliminado.", severity: "success" });
            } else {
                setSnack({ open: true, msg: r?.mensaje || "Error al eliminar.", severity: "error" });
            }
        } catch (err) {
            setSnack({ open: true, msg: err?.message || "Error de red.", severity: "error" });
        } finally {
            setConfirmOpen(false);
            setToDelete(null);
        }
    };

    return (
        <Box>
            {loading && <Box p={3} align="center"><CircularProgress /></Box>}
            {/* Aviso de bloqueo por Plan de Pago */}
            {!loading && bloqueadoProv && (
                <Box mb={2}>
                    <Alert severity="warning">
                        Expediente con Plan de Pago DGR. La grilla Provincia está bloqueada.
                        {bloqueadoCont ? " Contraparte/Auxiliares también bloqueados (pendiente de auditoría)." : ""}
                    </Alert>
                </Box>
            )}
            {!loading && !bloqueadoProv && bloqueadoCont && (
                <Box mb={2}>
                    <Alert severity="warning">Plan de Pago DGR pendiente de auditoría. Contraparte y Auxiliares bloqueados.</Alert>
                </Box>
            )}
            {/* Aviso soft si no tiene SELECT */}
            {!loading && permReady && !permProvincia.select && !permContraparte.select && (
                <Box mb={2}>
                    <Alert severity="warning">No tenés permiso para ver Partes. Es posible que el listado aparezca vacío.</Alert>
                </Box>
            )}
            {!loading && TABS.map(tab => {
                const gPerm = permParaGrilla(tab.grilla);
                const grillaBloqueada = bloqueadoParaGrilla(tab.grilla);
                const canAdd = gPerm.insert && !grillaBloqueada;
                return (
                <Card key={tab.key} style={{ marginBottom: 24, boxShadow: "0 2px 8px rgba(0,0,0,0.05)" }}>
                    <CardHeader
                        title={tab.label}
                        titleTypographyProps={{ style: { color: "#fff", fontWeight: 600, fontSize: 18, letterSpacing: 1 } }}
                        style={{ background: "#5d7b9d", borderTopLeftRadius: 4, borderTopRightRadius: 4, minHeight: 48 }}
                        action={
                            <>
                                <IconButton
                                    onClick={() => setTabOpen(prev => ({ ...prev, [tab.key]: !prev[tab.key] }))}
                                    style={{ color: "#fff" }}
                                    size="large">
                                    {tabOpen[tab.key] ? <ExpandLess /> : <ExpandMore />}
                                </IconButton>
                                <Tooltip title={grillaBloqueada ? "Bloqueado por Plan de Pago" : (canAdd ? "Agregar" : "Sin permiso para agregar")}>
                                    <span>
                                        <Button
                                            variant="contained"
                                            style={{ background: "#fff", color: "#5d7b9d", fontWeight: 600, marginLeft: 12, textTransform: "none" }}
                                            size="small"
                                            startIcon={<Add style={{ color: "#5d7b9d" }} />}
                                            onClick={() => handleAdd(tab.key, tab.grilla)}
                                            disabled={!canAdd}
                                        >
                                            Agregar
                                        </Button>
                                    </span>
                                </Tooltip>
                            </>
                        }
                    />
                    <Collapse in={tabOpen[tab.key]}>
                        <CardContent style={{ padding: 0 }}>
                            <PartesTable
                                rows={partes[tab.key]}
                                onEdit={(row) => handleEdit(tab.key, row, tab.grilla)}
                                onDelete={(row) => askDelete(tab.key, row)}
                                perm={{ ...gPerm, insert: canAdd, update: gPerm.update && !grillaBloqueada, delete: gPerm.delete && !grillaBloqueada }}
                            />
                        </CardContent>
                    </Collapse>
                </Card>
                );
            })}
            <ParteModal
                open={modalOpen}
                data={editData}
                onClose={() => setModalOpen(false)}
                onSave={handleSave}
                caracteres={caracteres}
                personas={personas}
                fetchPersonas={fetchPersonas}
                loadingPersonas={loadingPersonas}
                reparticiones={reparticiones}
                fetchReparticiones={fetchReparticiones}
                limpiarReparticiones={() => setReparticiones([])}
                loadingReparticiones={loadingReparticiones}
                contrapartes={contrapartes}
                fetchContrapartes={fetchContrapartes}
                loadingContrapartes={loadingContrapartes}
                idExpediente={idExpediente}
                setSnack={setSnack}
            />
            {/* Confirm delete */}
            <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)}>
                <DialogTitle style={{ background: "#5d7b9d", color: "#fff", textAlign: "center" }}>
                    ¿Eliminar parte?
                </DialogTitle>
                <DialogContent style={{ padding: "18px 24px" }}>
                    <Box mb={1}>Vas a eliminar el registro siguiente:</Box>
                    <Box bgcolor="#f9f9f9" p={2} borderRadius="4px">
                        <Grid container spacing={1}>
                            <Grid size={{ xs: 12, sm: 6 }}><b>Carácter:</b> {capitalizar(toDelete?.row?.caracter || "-")}</Grid>
                            <Grid size={{ xs: 12, sm: 6 }}><b>Persona:</b> {capitalizar(toDelete?.row?.persona || "-")}</Grid>
                            <Grid size={{ xs: 12, sm: 6 }}><b>DNI/CUIT:</b> {toDelete?.row?.dni || "-"}</Grid>
                            <Grid size={{ xs: 12, sm: 6 }}><b>Matrícula:</b> {toDelete?.row?.matricula || "-"}</Grid>
                        </Grid>
                    </Box>
                    <Box color="#b00020" mt={1}>Esta acción no se puede deshacer. Se registrará en el historial.</Box>
                </DialogContent>
                <DialogActions style={{ background: "#f5f5f5", padding: "12px 22px" }}>
                    <Button onClick={() => setConfirmOpen(false)} color="primary" variant="outlined">Cancelar</Button>
                    <Button onClick={confirmDelete} color="secondary" variant="contained" style={{ fontWeight: 700 }}>Eliminar</Button>
                </DialogActions>
            </Dialog>
            <Snackbar
                open={snack.open}
                autoHideDuration={3000}
                onClose={() => setSnack({ ...snack, open: false })}
                anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
            >
                <Alert severity={snack.severity}>{snack.msg}</Alert>
            </Snackbar>
        </Box>
    );
}

function PartesTable({ rows, onEdit, onDelete, perm }) {
    return (
        <TableContainer>
            <Table size="small">
                <TableHead>
                    <TableRow>
                        <TableCell>Carácter</TableCell>
                        <TableCell>Persona</TableCell>
                        <TableCell>DNI/CUIT</TableCell>
                        <TableCell>Dirección</TableCell>
                        <TableCell>Matrícula</TableCell>
                        {perm.update && <TableCell align="center">Editar</TableCell>}
                        {perm.delete && <TableCell align="center">Eliminar</TableCell>}
                    </TableRow>
                </TableHead>
                <TableBody>
                    {rows.map((row, i) => (
                        <TableRow key={row.id} style={{ background: i % 2 ? "#f4f6fa" : "#fff", cursor: "pointer" }} hover>
                            <TableCell>{capitalizar(row.caracter)}</TableCell>
                            <TableCell>{capitalizar(row.persona)}</TableCell>
                            <TableCell>{row.dni}</TableCell>
                            <TableCell>{capitalizar(row.direccion)}</TableCell>
                            <TableCell>{row.matricula}</TableCell>

                            {perm.update && (
                                <TableCell align="center">
                                    <Tooltip title={perm.update ? "Editar" : "Sin permiso"}>
                                        <span>
                                            <IconButton size="small" onClick={() => onEdit(row)} disabled={!perm.update}>
                                                <Edit style={{ color: "#1976d2" }} />
                                            </IconButton>
                                        </span>
                                    </Tooltip>
                                </TableCell>
                            )}

                            {perm.delete && (
                                <TableCell align="center">
                                    <Tooltip title={perm.delete ? "Eliminar" : "Sin permiso"}>
                                        <span>
                                            <IconButton size="small" onClick={() => onDelete(row)} disabled={!perm.delete}>
                                                <Delete style={{ color: "#e53935" }} />
                                            </IconButton>
                                        </span>
                                    </Tooltip>
                                </TableCell>
                            )}
                        </TableRow>
                    ))}
                    {rows.length === 0 && (
                        <TableRow>
                            <TableCell colSpan={perm.update || perm.delete ? 7 : 5} align="center" style={{ color: "#999" }}>
                                No hay registros cargados.
                            </TableCell>
                        </TableRow>
                    )}
                </TableBody>
            </Table>
        </TableContainer>
    );
}

function ParteModal({
    open, data, onClose, onSave, caracteres,
    personas, fetchPersonas, loadingPersonas,
    reparticiones, fetchReparticiones, loadingReparticiones, limpiarReparticiones,
    contrapartes, fetchContrapartes, loadingContrapartes,
    idExpediente, setSnack
}) {
    const [form, setForm] = useState({
        id_caracter: "",
        id_personal: "",
        id_reparticion: "",
        id_contraparte: "",
        sel_personal: null,
        sel_reparticion: null,
        sel_contraparte: null,
        direccion: "",
        cp: "",
    });
    const firstInputRef = useRef(null);

    // Estado para crear contraparte nueva
    const [crearContraparteOpen, setCrearContraparteOpen] = useState(false);
    const [nuevaContraparte, setNuevaContraparte] = useState({
        id: "", razon_social: "", nombre: "", direccion: "", cp: "", matricula: "", id_tipo_documento: ""
    });
    const [creandoContraparte, setCreandoContraparte] = useState(false);

    useEffect(() => {
        if (data) {
            setForm({
                id_caracter: data.id_caracter || "",
                id_personal: data.id_personal || "",
                id_reparticion: data.id_reparticion || "",
                id_contraparte: data.id_contraparte || "",
                sel_personal: data.id_personal ? { id: data.id_personal, nombre: data.persona } : null,
                sel_reparticion: data.id_reparticion ? { id: data.id_reparticion, nombre: data.persona } : null,
                sel_contraparte: data.id_contraparte ? { id: data.id_contraparte, nombre: data.persona } : null,
                direccion: data.direccion || "",
                cp: data.cp || "",
            });
            setCrearContraparteOpen(false);
        }
        if (open && firstInputRef.current) {
            setTimeout(() => firstInputRef.current.querySelector("input")?.focus(), 250);
        }
    }, [data, open]);

    const labelDe = (opt = {}) => {
        const id = String(opt.id ?? opt.Id ?? "");
        // Priorizar label (Apellido + Nombre completo del backend), luego apellido+nombre, luego nombre solo
        const nombrePlano =
            opt.label || opt.Persona || opt.persona ||
            (opt.apellido || opt.Apellido ? `${opt.apellido || opt.Apellido || ''} ${opt.nombre || opt.Nombre || ''}`.trim() : "") ||
            opt.razon_social || opt.Razon_Social ||
            opt.reparticion || opt.Reparticion ||
            opt.nombre || opt.Nombre || "";
        // Agregar matrícula si existe (para abogados)
        const mat = opt.matricula || opt.Matricula || "";
        const partes = [nombrePlano];
        if (mat) partes.push(`Mat: ${mat}`);
        if (id) partes.push(`(${id})`);
        return partes.join(" - ");
    };

    useEffect(() => {
        if (!open) return;
        const handleEsc = e => { if (e.key === "Escape") onClose(); };
        window.addEventListener("keydown", handleEsc);
        return () => window.removeEventListener("keydown", handleEsc);
    }, [open, onClose]);

    const grilla = data?.grilla;
    const getSelected = (list, id) => list.find(x => String(x.id) === String(id)) || null;
    const caracteresFiltrados = caracteres.filter(c => Number(c.grilla) === Number(grilla));

    const selCar = getSelected(caracteresFiltrados, form.id_caracter);
    // Tabla_Relacionada: 1=Repartición, 2=Personal, 3=Contraparte
    const tablaRel = selCar?.tablaRelacionada ?? selCar?.tabla_relacionada ?? null;
    const esProvRep = tablaRel === 1;

    // Tipo persona derivado del DNI/CUIT por longitud.
    // 8 dígitos = persona física con DNI; 11 dígitos = persona jurídica con CUIT.
    const docLen = String(nuevaContraparte.id || '').replace(/\D/g, '').length;
    const tipoPersonaContrap = docLen === 8 ? 'fisica' : docLen === 11 ? 'juridica' : null;
    const labelDoc = tipoPersonaContrap === 'fisica' ? 'DNI' : tipoPersonaContrap === 'juridica' ? 'CUIT' : 'DNI/CUIT';
    const labelRazon = tipoPersonaContrap === 'fisica' ? 'Apellido' : tipoPersonaContrap === 'juridica' ? 'Razón Social' : 'Apellido / Razón Social';

    // Crear contraparte nueva inline
    const handleCrearContraparte = async () => {
        if (!nuevaContraparte.id || !nuevaContraparte.razon_social) {
            setSnack({ open: true, msg: `${labelDoc} y ${labelRazon} son requeridos.`, severity: "warning" });
            return;
        }
        setCreandoContraparte(true);
        try {
            // Id_Tipo_Documento: letra del catálogo Tipos_Documento (A = DNI, K = CUIT).
            // NO mandar el código del Poder Judicial (1 / 11): el combo del sistema viejo
            // solo conoce las letras y un número lo hace explotar.
            const tipoDocAuto = tipoPersonaContrap === 'fisica' ? 'A' : tipoPersonaContrap === 'juridica' ? 'K' : '';
            const r = await crearContraparteJWT(idExpediente, {
                id: parseInt(nuevaContraparte.id),
                razonSocial: nuevaContraparte.razon_social,
                nombre: nuevaContraparte.nombre,
                direccion: nuevaContraparte.direccion,
                cp: nuevaContraparte.cp,
                matricula: nuevaContraparte.matricula,
                idTipoDocumento: nuevaContraparte.id_tipo_documento || tipoDocAuto,
            });
            if (r?.ok) {
                const nuevo = {
                    id: parseInt(nuevaContraparte.id),
                    label: `${nuevaContraparte.razon_social} ${nuevaContraparte.nombre}`.trim(),
                    razon_social: nuevaContraparte.razon_social,
                    nombre: nuevaContraparte.nombre,
                    direccion: nuevaContraparte.direccion,
                    matricula: nuevaContraparte.matricula,
                };
                setForm(prev => ({
                    ...prev,
                    id_contraparte: nuevo.id,
                    sel_contraparte: nuevo,
                    direccion: nuevaContraparte.direccion,
                    cp: nuevaContraparte.cp,
                }));
                setCrearContraparteOpen(false);
                setNuevaContraparte({ id: "", razon_social: "", nombre: "", direccion: "", cp: "", matricula: "", id_tipo_documento: "" });
                setSnack({ open: true, msg: "Contraparte creada.", severity: "success" });
            } else {
                setSnack({ open: true, msg: r?.mensaje || "Error al crear contraparte.", severity: "error" });
            }
        } catch (err) {
            setSnack({ open: true, msg: err?.message || "Error de red.", severity: "error" });
        } finally {
            setCreandoContraparte(false);
        }
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle style={{ background: "#5d7b9d", color: "#fff" }}>
                {data?.id ? "Editar Parte" : "Agregar Parte"}
            </DialogTitle>
            <DialogContent>
                <Box display="flex" flexDirection="column" gap={2}>
                    <Autocomplete
                        ref={firstInputRef}
                        options={caracteresFiltrados}
                        getOptionLabel={opt => capitalizar(opt.label || "")}
                        value={getSelected(caracteresFiltrados, form.id_caracter)}
                        onChange={(e, val) => {
                            const idc = val ? val.id : "";
                            // Tabla_Relacionada: 1=Repartición, 2=Personal
                            const exigeRep = (val?.tablaRelacionada ?? val?.tabla_relacionada) === 1;
                            // Las reparticiones se filtran por carácter: si cambió, la elegida
                            // puede no ser válida para el nuevo y las opciones cargadas tampoco.
                            const cambioCaracter = String(idc) !== String(form.id_caracter);
                            if (cambioCaracter) limpiarReparticiones?.();
                            setForm(prev => ({
                                ...prev,
                                id_caracter: idc,
                                id_personal: exigeRep ? "" : prev.id_personal,
                                sel_personal: exigeRep ? null : prev.sel_personal,
                                id_reparticion: exigeRep && !cambioCaracter ? prev.id_reparticion : "",
                                sel_reparticion: exigeRep && !cambioCaracter ? prev.sel_reparticion : null
                            }));
                        }}
                        renderInput={params => (<TextField {...params} label="Carácter" margin="dense" fullWidth />)}
                    />

                    {grilla === 1 && (
                        esProvRep ? (
                            // SOLO Repartición
                            (<Autocomplete
                                options={reparticiones}
                                filterOptions={(x) => x}
                                getOptionLabel= { opt => capitalizar(labelDe(opt))}
                                isOptionEqualToValue={(o, v) => String(o.id ?? o.Id) === String(v.id ?? v.Id)}
                                loading={loadingReparticiones}
                                value={form.sel_reparticion}
                                onInputChange={(e, value, reason) => { if (reason === "input" && value.length >= 2) fetchReparticiones(value, form.id_caracter); }}
                                noOptionsText="Sin reparticiones para este carácter en este módulo"
                                onChange={(e, val) => setForm(prev => ({
                                    ...prev,
                                    id_reparticion: val ? (val.id ?? val.Id) : "",
                                sel_reparticion: val,
                                // si elijo repartición, limpio persona
                                id_personal: "",
                                sel_personal: null
                                }))}
                                renderInput={params => (<TextField {...params} label="Repartición (buscar nombre/CUIT)" margin="dense" fullWidth />)}
                            />)
                        ) : (
                            // SOLO Persona
                                (<Autocomplete
                                    options={personas}
                                    filterOptions={(x) => x}
                                    getOptionLabel= { opt => capitalizar(labelDe(opt))}
                                    isOptionEqualToValue={(o, v) => String(o.id ?? o.Id) === String(v.id ?? v.Id)}
                                    loading={loadingPersonas}
                                    value={form.sel_personal}
                                    onInputChange={(e, value, reason) => { if (reason === "input" && value.length >= 2) fetchPersonas(value); }}
                                    onChange={(e, val) => setForm(prev => ({
                                    ...prev,
                                    id_personal: val ? (val.id ?? val.Id) : "",
                                    sel_personal: val,
                                    // si elijo persona, limpio repartición
                                    id_reparticion: "",
                                    sel_reparticion: null
                                }))}
                                    renderInput={params => (<TextField {...params} label="Persona (buscar nombre/DNI)" margin="dense" fullWidth />)}
                                />)
                        )
                    )}

                    {(grilla === 2 || grilla === 3) && (
                        <>
                            <Box display="flex" alignItems="center" gap={1}>
                                <Autocomplete
                                    sx={{ flex: 1 }}
                                    options={contrapartes}
                                    filterOptions={(x) => x}
                                    getOptionLabel={opt => capitalizar(labelDe(opt))}
                                    isOptionEqualToValue={(o, v) => String(o.id ?? o.Id) === String(v.id ?? v.Id)}
                                    loading={loadingContrapartes}
                                    value={form.sel_contraparte}
                                    onInputChange={(e, value, reason) => { if (reason === "input" && value.length >= 2) fetchContrapartes(value); }}
                                    onChange={(e, val) => {
                                        setForm(prev => ({
                                            ...prev,
                                            id_contraparte: val ? (val.id ?? val.Id) : "",
                                            sel_contraparte: val,
                                            direccion: val?.direccion || "",
                                            cp: val?.cp || "",
                                        }));
                                    }}
                                    renderInput={params => (<TextField {...params} label="Contraparte (buscar nombre/Razón Social/DNI)" margin="dense" fullWidth />)}
                                />
                                <Tooltip title="Crear contraparte nueva">
                                    <IconButton
                                        onClick={() => setCrearContraparteOpen(!crearContraparteOpen)}
                                        color={crearContraparteOpen ? "primary" : "default"}
                                        size="small"
                                        sx={{ mt: 1 }}
                                    >
                                        <PersonAdd />
                                    </IconButton>
                                </Tooltip>
                            </Box>

                            {/* Formulario para crear contraparte nueva */}
                            <Collapse in={crearContraparteOpen}>
                                <Box sx={{ p: 2, bgcolor: "#f5f7fa", borderRadius: 1, border: "1px solid #dde" }}>
                                    <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                                        Nueva Contraparte
                                    </Typography>
                                    <Grid container spacing={1}>
                                        <Grid size={{ xs: 12, sm: tipoPersonaContrap === 'juridica' ? 6 : 4 }}>
                                            <TextField
                                                label={labelDoc}
                                                value={nuevaContraparte.id}
                                                onChange={e => setNuevaContraparte(p => ({ ...p, id: e.target.value }))}
                                                size="small" fullWidth margin="dense" type="number"
                                                helperText={tipoPersonaContrap === 'fisica' ? 'Persona física (8 dígitos)' : tipoPersonaContrap === 'juridica' ? 'Persona jurídica (11 dígitos)' : ' '}
                                            />
                                        </Grid>
                                        <Grid size={{ xs: 12, sm: tipoPersonaContrap === 'juridica' ? 6 : 4 }}>
                                            <TextField
                                                label={labelRazon}
                                                value={nuevaContraparte.razon_social}
                                                onChange={e => setNuevaContraparte(p => ({ ...p, razon_social: e.target.value }))}
                                                size="small" fullWidth margin="dense"
                                            />
                                        </Grid>
                                        {tipoPersonaContrap !== 'juridica' && (
                                            <Grid size={{ xs: 12, sm: 4 }}>
                                                <TextField
                                                    label="Nombre"
                                                    value={nuevaContraparte.nombre}
                                                    onChange={e => setNuevaContraparte(p => ({ ...p, nombre: e.target.value }))}
                                                    size="small" fullWidth margin="dense"
                                                />
                                            </Grid>
                                        )}
                                        <Grid size={{ xs: 12, sm: 6 }}>
                                            <TextField
                                                label="Dirección"
                                                value={nuevaContraparte.direccion}
                                                onChange={e => setNuevaContraparte(p => ({ ...p, direccion: e.target.value }))}
                                                size="small" fullWidth margin="dense"
                                            />
                                        </Grid>
                                        <Grid size={{ xs: 12, sm: 3 }}>
                                            <TextField
                                                label="Código Postal"
                                                value={nuevaContraparte.cp}
                                                onChange={e => setNuevaContraparte(p => ({ ...p, cp: e.target.value }))}
                                                size="small" fullWidth margin="dense"
                                            />
                                        </Grid>
                                        <Grid size={{ xs: 12, sm: 3 }}>
                                            <TextField
                                                label="Matrícula"
                                                value={nuevaContraparte.matricula}
                                                onChange={e => setNuevaContraparte(p => ({ ...p, matricula: e.target.value }))}
                                                size="small" fullWidth margin="dense"
                                            />
                                        </Grid>
                                    </Grid>
                                    <Box display="flex" justifyContent="flex-end" mt={1}>
                                        <Button
                                            variant="contained" size="small"
                                            onClick={handleCrearContraparte}
                                            disabled={creandoContraparte || !nuevaContraparte.id || !nuevaContraparte.razon_social}
                                            sx={{ textTransform: "none" }}
                                        >
                                            {creandoContraparte ? <CircularProgress size={18} /> : "Crear Contraparte"}
                                        </Button>
                                    </Box>
                                </Box>
                            </Collapse>

                            {/* Dirección específica del expediente */}
                            <Divider sx={{ my: 0.5 }} />
                            <TextField
                                label="Dirección en este expediente"
                                value={form.direccion}
                                onChange={e => setForm(prev => ({ ...prev, direccion: e.target.value }))}
                                size="small" fullWidth margin="dense"
                                helperText="Dirección específica para este expediente (opcional)"
                            />
                        </>
                    )}
                </Box>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Cancelar</Button>
                <Button
                    color="primary"
                    variant="contained"
                    onClick={() => onSave({ ...form, id: data?.id, grilla })}
                    disabled={!form.id_caracter || (!form.id_personal && !form.id_reparticion && !form.id_contraparte)}
                >
                    OK
                </Button>
            </DialogActions>
        </Dialog>
    );
}
